import { Popover, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { flyoutPopoverProps } from './MenuFlyout';

/**
 * A small dialog that opens beside the menu holding `anchorEl`, as a menu
 * flyout does, for content a menu cannot hold: controls the user tabs
 * between and buttons that commit or discard them. It looks like a menu.
 * Tab stays inside it; Escape, Left Arrow and a click outside close it, and
 * focus goes back to the row it opened from. The pointer passes through to
 * the menu under it.
 */
export default function DialogFlyout({
    anchorEl,
    open,
    onClose,
    width,
    children,
    'aria-label': ariaLabel,
    'data-testid': testId,
}: {
    /** The menu row the flyout opens from. */
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    width: number;
    children: ReactNode;
    'aria-label': string;
    'data-testid'?: string;
}) {
    const theme = useTheme();
    return (
        <Popover
            {...flyoutPopoverProps(anchorEl, open, onClose, theme)}
            data-testid={testId}
            slotProps={{
                paper: {
                    role: 'dialog',
                    'aria-label': ariaLabel,
                    elevation: 1,
                    sx: {
                        pointerEvents: 'auto',
                        width,
                        maxWidth: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        p: 0.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: `${theme.shape.borderRadius}px`,
                    },
                },
            }}
        >
            {children}
        </Popover>
    );
}
